// The time book is kept on Portuguese time: API dates are Lisbon calendar days
// (YYYY-MM-DD) and every time is shown in Lisbon, whatever the device says.
const LISBON = "Europe/Lisbon";

/** Date/time display language: Chinese when the portal is in Chinese, else
 * Portuguese (the locale is stored by LocaleProvider; switching reloads). */
const uiLocale = (): string => {
  try {
    // Stored raw ("zh-Hans") or JSON-quoted, depending on the writer.
    const stored = (window.localStorage.getItem("locale") ?? "").replace(/"/g, "");

    return stored.startsWith("zh") ? "zh-CN" : "pt-PT";
  } catch {
    return "pt-PT";
  }
};

export const isoDay = (date: Date): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: LISBON,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

export const today = () => isoDay(new Date());

export const addDays = (day: string, days: number): string => {
  const date = new Date(`${day}T12:00:00Z`);

  date.setUTCDate(date.getUTCDate() + days);

  return date.toISOString().slice(0, 10);
};

/** Monday of the week containing ``day``. */
export const weekStart = (day: string): string => {
  const weekday = new Date(`${day}T12:00:00Z`).getUTCDay(); // 0 = Sunday

  return addDays(day, -((weekday + 6) % 7));
};

export const formatTime = (iso: string) =>
  new Intl.DateTimeFormat(uiLocale(), {
    timeZone: LISBON,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(iso));

export const formatDateTime = (iso: string) =>
  new Intl.DateTimeFormat(uiLocale(), {
    timeZone: LISBON,
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(iso));

export const formatDay = (day: string) =>
  new Intl.DateTimeFormat(uiLocale(), {
    timeZone: "UTC",
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }).format(new Date(`${day}T12:00:00Z`));

export const formatMinutes = (minutes: number, signed = false) => {
  const sign = minutes < 0 ? "-" : signed && minutes > 0 ? "+" : "";
  const abs = Math.abs(minutes);

  return `${sign}${Math.floor(abs / 60)}h${String(abs % 60).padStart(2, "0")}`;
};

const csvCell = (value: unknown) => {
  const text = value === null || value === undefined ? "" : String(value);

  return /[",;\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Offer rows as a CSV download (semicolon-separated, opens in PT Excel). */
export const downloadCsv = (filename: string, rows: unknown[][]) => {
  const body = "﻿" + rows.map(row => row.map(csvCell).join(";")).join("\r\n");
  const url = URL.createObjectURL(new Blob([body], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export interface Position {
  latitude: number;
  longitude: number;
  accuracyM: number;
}

/** One position fix at punch time (no tracking); null if denied/unavailable. */
export const currentPosition = (): Promise<Position | null> =>
  new Promise(resolve => {
    if (!navigator.geolocation) {
      resolve(null);

      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracyM: Math.round(coords.accuracy),
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });

/** "2026-10-13" + "09:30" on Lisbon time → ISO timestamp with the right offset. */
export const lisbonToIso = (day: string, hhmm: string) => {
  const guess = new Date(`${day}T${hhmm}:00Z`);
  const lisbon = new Date(guess.toLocaleString("en-US", { timeZone: LISBON }));
  const utc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));

  return new Date(guess.getTime() - (lisbon.getTime() - utc.getTime())).toISOString();
};

/** Short weekday name (0 = Monday) in the UI language. */
export const weekdayShort = (locale: string, weekday: number) =>
  new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(2026, 9, 12 + weekday)),
  );
